/**
 * Rank `keys` by a magnitude (descending) and split them into a "big" head (each ≥ `threshPct` of
 * the grand total) and a "small" tail (the caller folds these into a single "Other" series). Keys
 * whose magnitude is not positive drop out entirely.
 *
 * Pure — no ECharts or component state — so any breakdown-style chart can reuse the same
 * Top-N-with-"Other" reduction. Extracted from the resource-flow host.
 *
 * @param keys       the candidate series keys (e.g. split_group values)
 * @param magnitudeOf a stable, non-negative size for a key (e.g. its total over the range)
 * @param threshPct  share of the grand total below which a key is folded into "Other" (e.g. 0.01)
 */
export function topNWithOther<K extends string>(
    keys: K[],
    magnitudeOf: (key: K) => number,
    threshPct: number
): { big: K[]; small: K[] } {
    const ranked = keys
        .filter((k) => magnitudeOf(k) > 0)
        .sort((a, b) => magnitudeOf(b) - magnitudeOf(a));
    const threshold =
        (ranked.reduce((t, k) => t + magnitudeOf(k), 0) || 1) * threshPct;
    return {
        big: ranked.filter((k) => magnitudeOf(k) >= threshold),
        small: ranked.filter((k) => magnitudeOf(k) < threshold),
    };
}
